import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  Card,
  TextField,
  Autocomplete,
  ButtonGroup,
  Chip,
  Menu,
  MenuItem,
  InputAdornment,
  Collapse,
  IconButton,
  Switch,
  FormControlLabel,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import PaymentsIcon from "@mui/icons-material/Payments";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import CalculateIcon from "@mui/icons-material/Calculate";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import { styled } from "@mui/material/styles";
import jsPDF from 'jspdf';
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

// Components
import BaseWrapper from "../../components/shared/BaseWrapper";
import RateMultipliersSection from "./FormComponents/RateMultipliersSection";
import AdditionalIncomesSection from "./FormComponents/AdditionalIncomesSection";
import DeductionsSection from "./FormComponents/DeductionsSection";

// Utils
import { 
  getInitialState, 
  getMinEndDate, 
  getAllDatesInRange,
  formatHourlyRateForDisplay,
  formatAmountForDisplay,
  getDisplayValue,
  getFormTypeSegment,
  saveStateToStorage,
  getDefaultDateRange,
  spansToPayrollRows,
  clearStateFromStorage,
  getFormattedDateTime
} from "./utils/payrollHelpers";
import { 
  calculateNightDiffForDate,
  calculateDailyHours,
  calculateTotalHours,
  hasActualJobOrders,
  hasJobOrders,
  countJobsForDisplay,
  groupPayrollDataByDate,
  sortDates,
  categorizeDates
} from "./utils/payrollCalculations";
import { 
  handleHourlyRateInputChange,
  handleRateMultiplierInputChange,
  handleAdditionalIncomeInputChange,
  handleDeductionInputChange,
  handleCustomIncomeEntryChange,
  handleCustomDeductionEntryChange,
  handleLoanBalanceEntryChange,
  handleLeaveBalanceEntryChange,
  handleAdditionalHoursInputChange
} from "./utils/payrollInputHandlers";
import { 
  handlePaidLeaveToggleLogic,
  handleSaveBreakdownLogic
} from "./utils/payrollBreakdownHandlers";
import { handlePayrollSearch } from "./utils/payrollSearchHandler";
import { 
  calculateNightDifferentialFromSpans,
  calculateNightDifferentialHours,
  sumUniqueMinutesFromSpans
} from "./utils/payrollUniqueTime";
import { fetchAndSplitJobOrdersForPayroll } from "./utils/PayrollJobOrderFilter";
import { formatTo12Hour } from "../ServiceReports/utils/timeFormatter";

// API
import { getCacheTechnicians } from "../API/technicianAPI";
import { getJobOrdersForPayroll } from "../API/payrollAPI";
import { getJobOrder, putJobOrder } from "../API/jobOrderAPI";
import { postJobOrderLogs } from "../API/jobOrderLogsAPI";
import { deductSILCredits, getTechnicianPayrateCredits } from "../API/technicianPayrateCreditsAPI";

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  opacity: 0.8,
}));

const toPhilippineIsoFromBase = (baseIso, fallbackDate, hhmm) => {
  if (!hhmm) return "";

  const [hour, minute] = String(hhmm).split(":").map(Number);

  let base;
  if (baseIso) {
    base = dayjs(baseIso);
  } else if (fallbackDate) {
    base = dayjs(`${fallbackDate}T00:00:00+08:00`);
  } else {
    return "";
  }

  return base
    .hour(hour)
    .minute(minute)
    .second(0)
    .millisecond(0)
    .toISOString();
};

function PayrollReports() {
  // Get initial saved state if available
  const initialSavedState = getInitialState();
  
  const [uniqueDayTotals, setUniqueDayTotals] = useState({});
  const [uniqueGrandTotalHours, setUniqueGrandTotalHours] = useState(0);
  const [spansByDate, setSpansByDate] = useState({}); // Store spans organized by date for night diff calculation
  const [payrollData, setPayrollData] = useState(initialSavedState?.payrollData || []);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [availableSilCredits, setAvailableSilCredits] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [techniciansCache, setTechniciansCache] = useState([]);
  const [hourlyRate, setHourlyRate] = useState(initialSavedState?.hourlyRate || "");
  const [finalCalcData, setFinalCalcData] = useState(null);
  const [expandedDates, setExpandedDates] = useState({});
  const [manualEntries, setManualEntries] = useState({});
  const [paidLeaveDates, setPaidLeaveDates] = useState(initialSavedState?.paidLeaveDates || {});
  const [hasSearched, setHasSearched] = useState(initialSavedState?.hasSearched || false);
  const [rateMultipliers, setRateMultipliers] = useState(initialSavedState?.rateMultipliers || {
    normalHours: "",
    nightDifferential: "",
    overtime: "",
    regularHoliday: "",
    specialHoliday: "",
    addWorkHours: "",
    customRates: {} // For custom entries: { label: multiplier }
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedJobForEdit, setSelectedJobForEdit] = useState(null);
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [additionalIncomes, setAdditionalIncomes] = useState(initialSavedState?.additionalIncomes || {
    allowance: "",
    bonus: "",
    thirteenthMonthPay: "",
    customIncomes: [] // Array of { label: "", amount: "" }
  });
  const [deductions, setDeductions] = useState(initialSavedState?.deductions || {
    totalDeductedHours: "",
    totalDeductedHoursMultiplier: "",
    sss: "",
    sssProvident: "",
    philhealth: "",
    pagIbig: "",
    unionDues: "",
    tax: "",
    pagIbig2: "",
    customDeductions: [], // Array of { label: "", amount: "" }
    loanBalances: [], // Array of { label: "", value: "" }
    leaveBalances: [] // Array of { label: "", value: "" }
  });
  const [focusedField, setFocusedField] = useState(null); // Track which field is currently focused
  const [nightStart, setNightStart] = useState(initialSavedState?.nightStart || "22:00");
  const [nightEnd, setNightEnd] = useState(initialSavedState?.nightEnd || "06:00");
  const [searchFilter, setSearchFilter] = useState(initialSavedState?.searchFilter || {
    employee: null,
    startDate: (() => {
      const now = new Date();
      const philippineTimezoneOffset = 8 * 60; // UTC+8 in minutes
      const localDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        1, // First day of the current month
        0,
        0,
        0,
        0
      );
      localDate.setMinutes(localDate.getMinutes() + philippineTimezoneOffset);
      return localDate.toISOString().split("T")[0];
    })(),
    endDate: (() => {
      const now = new Date();
      const philippineTimezoneOffset = 8 * 60; // UTC+8 in minutes
      const localDate = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0, // Last day of the current month
        0,
        0,
        0,
        0
      );
      localDate.setMinutes(localDate.getMinutes() + philippineTimezoneOffset);
      return localDate.toISOString().split("T")[0];
    })(),
  });

  const navigate = useNavigate();
  const location = useLocation();

  // Debug log to check component mount and location
  // useEffect(() => {
  //   console.log("PayrollReports component mounted");
  //   console.log("Current location:", location);
  //   console.log("Location search params:", location.search);
  //   console.log("Saved state in storage:", sessionStorage.getItem('payrollReportsState'));
  // }, [location]);

  useEffect(() => {
    // Initialize component - fetch technicians cache and clean up storage
    const initializePayroll = async () => {
      try {
        // Fetch technicians cache
        const techniciansCache = await getCacheTechnicians();
        setTechniciansCache(techniciansCache || []);
        
        // If we initialized with saved state, clean up storage now
        if (initialSavedState) {
          sessionStorage.removeItem('payrollReportsState');
          localStorage.removeItem('payrollReportsState');
        }
        
      } catch (err) {
        setError(err.message);
        console.error("Initialization error:", err);
      }
    };

    initializePayroll();
  }, [initialSavedState]);

  // Fetch SIL credits when employee is selected
  useEffect(() => {
    const fetchSilCredits = async () => {
      if (searchFilter?.employee?.id) {
        try {
          const data = await getTechnicianPayrateCredits(searchFilter.employee.id);
          if (data && data.leave_credits !== undefined) {
            setAvailableSilCredits(parseFloat(data.leave_credits) || 0);
          }
        } catch (error) {
          console.error("Error fetching SIL credits:", error);
        }
      } else {
        setAvailableSilCredits(null);
      }
    };

    fetchSilCredits();
  }, [searchFilter?.employee?.id]);

  // Auto-save state whenever key data changes
  useEffect(() => {
    // Only save if we have searched
    if (hasSearched) {
      const stateToSave = {
        version: '1.1', // Match version in getInitialState
        payrollData: payrollData,
        searchFilter: searchFilter,
        hourlyRate: hourlyRate,
        paidLeaveDates: paidLeaveDates,
        rateMultipliers: rateMultipliers,
        additionalIncomes: additionalIncomes,
        deductions: deductions,
        nightStart: nightStart,
        nightEnd: nightEnd,
        hasSearched: hasSearched,
        timestamp: Date.now()
      };
      sessionStorage.setItem('payrollReportsState', JSON.stringify(stateToSave));
      localStorage.setItem('payrollReportsState', JSON.stringify(stateToSave));
    }
  }, [payrollData, searchFilter, hourlyRate, paidLeaveDates, rateMultipliers, additionalIncomes, deductions, nightStart, nightEnd, hasSearched]);

  // Cleanup effect to clear saved state on normal unmount
  useEffect(() => {
    return () => {
      // Only clear if we're not navigating to any service report view
      const currentPath = window.location.pathname;
      const serviceReportPaths = [
        '/view-job-order/',
        '/view-makati-gpc-form/',
        '/view-qc-form/',
        '/view-termite-control-form/',
        '/view-transmittal-form/'
      ];
      
      const isNavigatingToServiceReport = serviceReportPaths.some(path => 
        currentPath.includes(path)
      );
      
      if (!isNavigatingToServiceReport) {
        sessionStorage.removeItem('payrollReportsState');
        localStorage.removeItem('payrollReportsState');
      }
    };
  }, []);

  const handleSearchButtonClick = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setErrorMessage("");
      setHasSearched(false); // Reset until search completes

      if (!searchFilter.employee) {
        setErrorMessage("Please select a technician to search for their job orders.");
        return;
      }

      const startDate = searchFilter.startDate;
      const endDate = searchFilter.endDate;

      // Preserve existing manual/breakdown/deduction entries within range
      const existingManualAndBreakdownEntries = payrollData.filter(job =>
        (job.isManual || job.isBreakdown || job.isDeduction) &&
        job.job_date >= startDate &&
        job.job_date <= endDate
      );

      // === Use the payroll-aware fetcher ===
      // console.log("🔍 [PAYROLL] Fetching job orders with parameters:", {
      //   startDate,
      //   endDate,
      //   technicianId: searchFilter.employee?.id || null,
      //   technicianName: searchFilter.employee?.name || null,
      //   status: [],
      //   serviceReportStatus: []
      // });
      
      const { flatSpans } = await fetchAndSplitJobOrdersForPayroll({
        searchFilter: { startDate, endDate },
        // Match your previous logic: service report must be Done; status unrestricted
        status: [],
        serviceReportStatus: [], // or ["Done"] if we only want completed SRs
        technicianId: searchFilter.employee?.id || null,
        getJobOrdersApi: getJobOrdersForPayroll,
      });
      
      // console.log("=== RAW SPAN DEBUG ===");
      // console.log("FULL SPAN:", JSON.stringify(flatSpans[0], null, 2));
      // console.log("form_type:", flatSpans[0]?.form_type);
      // console.log("raw.form_type:", flatSpans[0]?.raw?.form_type);
      // console.log("📊 [PAYROLL] Received spans:", flatSpans.length, "Total spans:", flatSpans);

      // Transform spans → rows your table already expects
      const payrollRows = spansToPayrollRows(flatSpans);

      // console.log("=== MAPPED ROW DEBUG ===");
      // console.log("PAYROLL ROW:", payrollRows[0]);
      // console.log("PAYROLL ROW form_type:", payrollRows[0]?.form_type);
      
      // Compute unique (no-overlap) minutes/hours per day ---
      const spansGroupedByDate = flatSpans.reduce((acc, s) => {
        (acc[s.payroll_date] ??= []).push(s);
        return acc;
      }, {});
      const dayTotals = {};
      let grandMinutes = 0;
      Object.entries(spansGroupedByDate).forEach(([d, spans]) => {
        const { minutes, hours } = sumUniqueMinutesFromSpans(spans); // already imported
        dayTotals[d] = { minutes, hours };
        grandMinutes += minutes;
      });
      setUniqueDayTotals(dayTotals);
      setUniqueGrandTotalHours(+(grandMinutes / 60).toFixed(2));
      setSpansByDate(spansGroupedByDate); // Store spans for night diff calculation

      // Merge fetched rows with preserved manual/breakdown/deduction entries
      const mergedResults = [...payrollRows, ...existingManualAndBreakdownEntries].sort((a, b) => {
        const dateA = new Date(a.job_date).getTime();
        const dateB = new Date(b.job_date).getTime();
        if (dateA !== dateB) return dateA - dateB;

        // Manual and breakdown entries go to the end of the same date
        if ((a.isManual || a.isBreakdown) && !(b.isManual || b.isBreakdown)) return 1;
        if (!(a.isManual || a.isBreakdown) && (b.isManual || b.isBreakdown)) return -1;
        return 0;
      });

      setPayrollData(mergedResults);
      setHasSearched(true);

      if (payrollRows.length === 0 && existingManualAndBreakdownEntries.length === 0) {
        setSuccessMessage(`Search completed. Showing all dates from ${startDate} to ${endDate}.`);
      } else {
        const totalJobs = payrollRows.length;
        const manualCount = existingManualAndBreakdownEntries.length;
        if (manualCount > 0) {
          setSuccessMessage(`Found ${totalJobs} job orders + ${manualCount} manual/breakdown entries for ${searchFilter.employee.name}.`);
        } else {
          setSuccessMessage(`Found ${totalJobs} job orders for ${searchFilter.employee.name}.`);
        }
      }
      // console.log("PAYROLL ROW:", payrollRows[0]);
    } catch (err) {
      console.error("Search error:", err);
      setError(err.message);
      setErrorMessage("Failed to fetch job orders. Please try again.");
      setHasSearched(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchEmployee = (event, value) => {
    setSearchFilter((prev) => ({
      ...prev,
      employee: value,
    }));
  };

  // Handle paid leave toggle
  const handlePaidLeaveToggle = (date) => {
    setPaidLeaveDates((prev) => {
      const newPaidLeaveDates = { ...prev };
      
      if (newPaidLeaveDates[date]) {
        // Remove paid leave marking
        delete newPaidLeaveDates[date];
        
        // Collapse the expanded section when toggling off
        setExpandedDates((prevExpanded) => ({
          ...prevExpanded,
          [date]: false
        }));
        
        // Clear any manual entries for this date when toggling off
        setManualEntries((prevManual) => {
          const newManual = { ...prevManual };
          delete newManual[date];
          return newManual;
        });
        
        // Delete all manual, breakdown, and paid leave entries for this date
        setPayrollData((prevData) => {
          return prevData.filter(job => {
            // Keep the job if it's NOT for this date OR if it's a regular job order
            return job.job_date !== date || (!job.isManual && !job.isBreakdown && !job.isPaidLeave);
          });
        });
        
        setSuccessMessage("Paid leave toggled off and all manual entries removed for this date.");
      } else {
        // Mark date as paid leave (doesn't create entry, just marks the date)
        newPaidLeaveDates[date] = true;
      }
      
      return newPaidLeaveDates;
    });
  };

  // Function to save current state before navigation
  const saveStateBeforeNavigation = () => {
    const stateToSave = {
      payrollData: payrollData,
      searchFilter: searchFilter,
      hourlyRate: hourlyRate,
      paidLeaveDates: paidLeaveDates,
      rateMultipliers: rateMultipliers,
      additionalIncomes: additionalIncomes,
      deductions: deductions,
      nightStart: nightStart,
      nightEnd: nightEnd,
      hasSearched: hasSearched,
      timestamp: Date.now() // Add timestamp for debugging
    };
    
    // Save to both session and local storage for redundancy
    sessionStorage.setItem('payrollReportsState', JSON.stringify(stateToSave));
    localStorage.setItem('payrollReportsState', JSON.stringify(stateToSave));
  };

  // Helper: build the service report route based on form_type
  const getServiceReportRoute = (job) => {
    const formTypeToSegment = {
      makati_gpc_form: "makati-gpc-form",
      qc_form: "qc-form",
      termite_control_form: "termite-control-form",
      transmittal_form: "transmittal-form",
    };

    const formType = job?.form_type;
    const segment = formTypeToSegment[formType];

    if (!segment) {
      console.warn("Missing or unknown form_type:", job);
      return `/view-job-order/${job.id}?from=payroll-reports`;
    }

    return `/view-${segment}/${job.id}?from=payroll-reports`;
  };

  const handleOpenServiceReportInNewTab = async (job) => {
    try {
      saveStateBeforeNavigation();

      const apiResponse = await getJobOrder(job.id);
      const jobDetails = extractJobDetails(apiResponse);

      const route = getServiceReportRoute({
        ...job,
        form_type: jobDetails?.form_type || job?.form_type || null,
      });

      // console.log("OPEN JOB:", job);
      // console.log("JOB DETAILS form_type:", jobDetails?.form_type);
      // console.log("OPEN ROUTE:", route);

      window.open(route, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Failed to resolve service report route:", error);
      window.open(`/view-job-order/${job.id}?from=payroll-reports`, "_blank", "noopener,noreferrer");
    }
  };

  // --- Edit dialog handlers ---
  const normalizeTimeForInput = (timeStr) => {
    if (!timeStr) return "";
    const parts = String(timeStr).split(":");
    const [hh, mm] = parts;
    if (!hh || !mm) return timeStr;
    return `${hh.padStart(2, "0")}:${mm.padStart(2, "0")}`;
  };

  // Safely extract the job details from getJobOrder's API response
  const extractJobDetails = (apiResponse) => {
    if (!apiResponse) return null;

    // If backend returns { job_order_data: [ { ...job } ] }
    if (Array.isArray(apiResponse.job_order_data) && apiResponse.job_order_data.length > 0) {
      return apiResponse.job_order_data[0];
    }

    // If backend returns { job_order_data: { ...job } }
    if (apiResponse.job_order_data && !Array.isArray(apiResponse.job_order_data)) {
      return apiResponse.job_order_data;
    }

    // If backend returns { Item: { ...job } } (DynamoDB style)
    if (apiResponse.Item) {
      return apiResponse.Item;
    }

    // Fallback: assume the response itself is the job
    return apiResponse;
  };

  const handleOpenEditTimesDialog = async (jobRow) => {
    try {
      setEditSaving(false);
      setErrorMessage("");
      setSuccessMessage("");

      // Always load the *original* job order from API using the job id
      const apiResponse = await getJobOrder(jobRow.id);
      const jobDetails = extractJobDetails(apiResponse);

      const isoToHHMM = (isoString) => {
        if (!isoString) return "";
        try {
          const d = new Date(isoString);
          return d.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: "Asia/Manila",
          });
        } catch {
          return "";
        }
      };

      if (!jobDetails) {
        setErrorMessage("Failed to load full job order details for editing.");
        return;
      }

      const derivedEndTime =
        isoToHHMM(jobDetails.client_signature_uploaded_at) ||
        jobDetails.end_time ||
        "";

      if (!jobDetails.start_time || !derivedEndTime) {
        setErrorMessage("Failed to load full job order details for editing.");
        return;
      }

      if (!jobDetails || !jobDetails.start_time || !derivedEndTime) {
        setErrorMessage("Failed to load full job order details for editing.");
        return;
      }

      setSelectedJobForEdit({
        ...jobRow,
        original_start_time: jobDetails.start_time,
        original_end_time: derivedEndTime,
        original_job_date: jobDetails.job_date,
        hasClientSignatureTimestamp: Boolean(jobDetails.client_signature_uploaded_at),
        raw_client_signature_uploaded_at: jobDetails.client_signature_uploaded_at || "",
        raw_end_time: jobDetails.end_time || "",
      });

      setEditStartTime(normalizeTimeForInput(jobDetails.start_time));
      setEditEndTime(normalizeTimeForInput(derivedEndTime));
      setEditDialogOpen(true);
    } catch (err) {
      // console.error("Error opening edit dialog with full job order:", err);
      setErrorMessage("Unable to load job order for editing. Please try again.");
    }
  };

  const handleCloseEditTimesDialog = () => {
    if (editSaving) return; // prevent closing while saving
    setEditDialogOpen(false);
    setSelectedJobForEdit(null);
    setEditStartTime("");
    setEditEndTime("");
  };

  const handleSaveEditedTimes = async () => {
    if (!selectedJobForEdit) return;

    if (!editStartTime || !editEndTime) {
      setErrorMessage("Start time and end time are required.");
      return;
    }

    const storedUser = localStorage.getItem("user");
    const user_update = storedUser ? JSON.parse(storedUser).username : "";

    if (!user_update) {
      setErrorMessage("Unable to identify the current user. Please log in again.");
      return;
    }

    const originalStartRaw =
      selectedJobForEdit.original_start_time || selectedJobForEdit.start_time || "";
    const originalEndRaw =
      selectedJobForEdit.original_end_time || selectedJobForEdit.end_time || "";

    const originalStartNorm = normalizeTimeForInput(originalStartRaw);
    const originalEndNorm = normalizeTimeForInput(originalEndRaw);

    // Build payload FIRST
    const updatePayload = {
      start_time: editStartTime,
      user_update,
    };

    if (selectedJobForEdit.hasClientSignatureTimestamp) {
      updatePayload.client_signature_uploaded_at = toPhilippineIsoFromBase(
        selectedJobForEdit.raw_client_signature_uploaded_at,
        selectedJobForEdit.original_job_date,
        editEndTime
      );
    } else {
      updatePayload.end_time = editEndTime;
    }

    // Then build logs
    const updatedFields = {};

    if (editStartTime !== originalStartNorm) {
      updatedFields.start_time = {
        old: originalStartRaw,
        new: editStartTime,
      };
    }

    if (editEndTime !== originalEndNorm) {
      if (selectedJobForEdit.hasClientSignatureTimestamp) {
        updatedFields.client_signature_uploaded_at = {
          old: selectedJobForEdit.raw_client_signature_uploaded_at || "",
          new: updatePayload.client_signature_uploaded_at,
        };
      } else {
        updatedFields.end_time = {
          old: selectedJobForEdit.raw_end_time || "",
          new: editEndTime,
        };
      }

      updatedFields.payroll_effective_end_time = {
        old: originalEndNorm,
        new: editEndTime,
      };
    }

    if (Object.keys(updatedFields).length === 0) {
      setSuccessMessage("No changes to save.");
      handleCloseEditTimesDialog();
      return;
    }

    try {
      setEditSaving(true);
      setErrorMessage("");
      setSuccessMessage("");

      const result = await putJobOrder(selectedJobForEdit.id, updatePayload);

      if (!result?.success) {
        const errMsg = result?.error?.message || "Failed to update job order.";
        setErrorMessage(errMsg);
        return;
      }

      try {
        await postJobOrderLogs(
          selectedJobForEdit.id,
          user_update,
          updatedFields
        );
      } catch (logError) {
        console.error("Job order updated but logging failed:", logError);
        setErrorMessage("Job order time updated, but logging failed. Please inform admin.");
      }

      setPayrollData((prev) =>
        prev.map((job) =>
          job.id === selectedJobForEdit.id
            ? {
                ...job,
                start_time: editStartTime,
                end_time: editEndTime,
                ...(selectedJobForEdit.hasClientSignatureTimestamp
                  ? {
                      client_signature_uploaded_at:
                        updatePayload.client_signature_uploaded_at,
                    }
                  : {}),
              }
            : job
        )
      );

      await handleSearchButtonClick();

      setSuccessMessage("Job order start/end time updated successfully.");
      handleCloseEditTimesDialog();
    } catch (error) {
      console.error("Error saving edited times:", error);
      setErrorMessage("An unexpected error occurred while saving changes.");
    } finally {
      setEditSaving(false);
    }
  };

  // Toggle expand/collapse for manual entry section
  const handleToggleExpand = (date, hasNoJobOrders) => {
    // If date has no job orders and paid leave is not toggled, don't allow expand
    if (hasNoJobOrders && !paidLeaveDates[date]) {
      return;
    }
    
    setExpandedDates((prev) => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  // Initialize manual entry form for a date if not exists
  const getManualEntryForDate = (date) => {
    if (!manualEntries[date]) {
      return {
        workHours: '',
        workHoursRemarks: '',
        deductHours: '',
        deductHoursRemarks: '',
        normalHours: '',
        normalHoursMultiplier: '',
        nightDifferential: '',
        nightDifferentialMultiplier: '',
        overtime: '',
        overtimeMultiplier: '',
        regularHoliday: '',
        regularHolidayMultiplier: '',
        specialHoliday: '',
        specialHolidayMultiplier: '',
        customEntries: [] // Array of {label: '', hours: '', id: uniqueId}
      };
    }
    return manualEntries[date];
  };

  // Helper wrapper for calculateNightDiffForDate with component state
  const calcNightDiffForDate = (date) => {
    return calculateNightDiffForDate(
      date, 
      spansByDate, 
      payrollData, 
      nightStart, 
      nightEnd, 
      calculateNightDifferentialFromSpans
    );
  };

  // Update manual entry field
  const handleManualEntryChange = (date, value) => {
    setManualEntries((prev) => ({
      ...prev,
      [date]: {
        ...getManualEntryForDate(date),
        workHours: value
      }
    }));
  };

  // Update additional hours fields - delegate to utility
  const handleAdditionalHoursChange = (date, field, value) => {
    handleAdditionalHoursInputChange(date, field, value, setManualEntries, getManualEntryForDate);
  };

  // Add manual entry to payroll data
  const handleAddManualEntry = (date) => {
    const entry = manualEntries[date];
    
    if (!entry || !entry.workHours || parseFloat(entry.workHours) <= 0) {
      setErrorMessage("Please enter valid work hours.");
      return;
    }

    // Get current date and time for timestamp
    const now = new Date();
    const formattedDateTime = now.toLocaleString('en-US', { 
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    // Create new manual job entry
    const newJob = {
      id: 'MANUAL',
      job_date: date,
      start_time: formattedDateTime,
      end_time: formattedDateTime,
      client: 'Manual Entry',
      job_types: entry.workHoursRemarks ? [`Manual Work Hours - ${entry.workHoursRemarks}`] : ['Manual Work Hours'],
      workHours: parseFloat(entry.workHours),
      regularHours: parseFloat(entry.workHours),
      remarks: entry.workHoursRemarks || '',
      isManual: true // Flag to identify manual entries
    };

    // Add to payroll data
    setPayrollData((prev) => {
      const updated = [...prev, newJob];
      // Sort by date and time
      return updated.sort((a, b) => {
        const dateA = new Date(a.job_date).getTime();
        const dateB = new Date(b.job_date).getTime();
        if (dateA !== dateB) return dateA - dateB;
        
        // Manual entries go to the end of the same date
        if (a.isManual && !b.isManual) return 1;
        if (!a.isManual && b.isManual) return -1;
        return 0;
      });
    });

    // Clear the form
    setManualEntries((prev) => ({
      ...prev,
      [date]: {
        ...getManualEntryForDate(date),
        workHours: '',
        workHoursRemarks: ''
      }
    }));

    setSuccessMessage("Manual work hours added successfully!");
  };

  // Deduct work hours from payroll data
  const handleDeductManualEntry = (date) => {
    const entry = manualEntries[date];
    
    if (!entry || !entry.deductHours || parseFloat(entry.deductHours) <= 0) {
      setErrorMessage("Please enter valid hours to deduct.");
      return;
    }

    // Get current date and time for timestamp
    const now = new Date();
    const formattedDateTime = now.toLocaleString('en-US', { 
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    // Create new deduction job entry
    const newJob = {
      // id: `DEDUCTION_${Date.now()}`, // Unique ID for deduction entries
      id: 'DEDUCTION',
      job_date: date,
      start_time: formattedDateTime,
      end_time: formattedDateTime,
      client: 'Deduction Entry',
      job_types: entry.deductHoursRemarks ? [`Deducted Hours - ${entry.deductHoursRemarks}`] : ['Deducted Hours'],
      workHours: parseFloat(entry.deductHours),
      regularHours: parseFloat(entry.deductHours),
      remarks: entry.deductHoursRemarks || '',
      isDeduction: true // Flag to identify deduction entries
    };

    // Add to payroll data
    setPayrollData((prev) => {
      const updated = [...prev, newJob];
      // Sort by date and time
      return updated.sort((a, b) => {
        if (a.job_date !== b.job_date) {
          return new Date(a.job_date) - new Date(b.job_date);
        }
        const timeA = new Date(`1970-01-01 ${a.start_time}`);
        const timeB = new Date(`1970-01-01 ${b.start_time}`);
        return timeA - timeB;
      });
    });

    // Clear the form
    setManualEntries((prev) => ({
      ...prev,
      [date]: {
        ...getManualEntryForDate(date),
        deductHours: '',
        deductHoursRemarks: ''
      }
    }));

    setSuccessMessage("Deducted hours added successfully!");
  };

  // Delete manual or breakdown entry from payroll data
  const handleDeleteManualEntry = (jobId, jobDate) => {
    setPayrollData((prev) => {
      const filtered = prev.filter(job => job.id !== jobId);
      
      // If this was a paid leave entry, also remove from paidLeaveDates
      if (jobId === 'PAID_LEAVE') {
        setPaidLeaveDates((prevDates) => {
          const newDates = { ...prevDates };
          delete newDates[jobDate];
          return newDates;
        });
      }
      
      return filtered;
    });
    
    setSuccessMessage("Entry deleted successfully!");
  };

  // Save breakdown to payroll data (adds as entry in main table)
  const handleSaveBreakdown = (date) => {
    const entry = getManualEntryForDate(date);
    const baseRate = parseFloat(hourlyRate) || 0;

    // Check if any SIL entry exceeds available credits
    const silEntries = (entry.customEntries || [])
      .filter(custom => custom != null && custom.labelType === 'sil');
    
    if (silEntries.length > 0 && availableSilCredits !== null) {
      const totalSilCredits = silEntries.reduce((sum, custom) => {
        return sum + (parseFloat(custom.hours) || 0);
      }, 0);
      
      if (totalSilCredits > availableSilCredits) {
        setErrorMessage(`Cannot save: Total SIL credits (${totalSilCredits}) exceeds available credits (${availableSilCredits})`);
        return;
      }
    }

    // Use manual night differential if provided, otherwise use auto-calculated
    const nightDiffHours = (entry.nightDifferential !== '' && entry.nightDifferential !== undefined)
      ? parseFloat(entry.nightDifferential) || 0
      : parseFloat(calcNightDiffForDate(date)) || 0;

    // Calculate total hours including custom entries
    // IMPORTANT: SIL "hours" input is actually CREDITS, so convert credits → hours for totals.
    const customEntriesHours = (entry.customEntries || [])
      .filter(custom => custom != null)
      .reduce((sum, custom) => {
        const raw = parseFloat(custom.hours) || 0;
        const isSIL =
          custom.labelType === "sil" ||
          (custom.label || "").trim() === "Service Incentive Leave";

        return sum + (isSIL ? raw * 8 : raw);
      }, 0);

    const distributedHours = (parseFloat(entry.normalHours) || 0) + 
                      nightDiffHours + 
                      (parseFloat(entry.overtime) || 0) + 
                      (parseFloat(entry.regularHoliday) || 0) + 
                      (parseFloat(entry.specialHoliday) || 0) +
                      customEntriesHours;
    
    if (distributedHours === 0) {
      setErrorMessage("Please enter hours to distribute before saving.");
      return;
    }

    // Build job types array with breakdown details
    const jobTypes = [];
    if (parseFloat(entry.normalHours) > 0) {
      jobTypes.push(`Normal Hours: ${entry.normalHours}h`);
    }
    if (nightDiffHours > 0) {
      jobTypes.push(`Night Diff: ${nightDiffHours.toFixed(2)}h`);
    }
    if (parseFloat(entry.overtime) > 0) {
      jobTypes.push(`Overtime: ${entry.overtime}h`);
    }
    if (parseFloat(entry.regularHoliday) > 0) {
      jobTypes.push(`Regular Holiday: ${entry.regularHoliday}h`);
    }
    if (parseFloat(entry.specialHoliday) > 0) {
      jobTypes.push(`Special Non-Working Holiday: ${entry.specialHoliday}h`);
    }
    // Add custom entries to job types
    // (entry.customEntries || [])
    //   .filter(custom => custom != null)
    //   .forEach(custom => {
    //     if (parseFloat(custom.hours) > 0 && custom.label) {
    //       jobTypes.push(`${custom.label}: ${custom.hours}h`);
    //     }
    //   });
    
    // Add custom entries to job types (show SIL as hours, not credits)
    (entry.customEntries || [])
      .filter(custom => custom != null)
      .forEach(custom => {
        const raw = parseFloat(custom.hours) || 0;
        if (raw > 0 && custom.label) {
          const isSIL =
            custom.labelType === "sil" ||
            (custom.label || "").trim() === "Service Incentive Leave";

          const displayHours = isSIL ? raw * 8 : raw;
          jobTypes.push(`${custom.label}: ${displayHours}h`);
        }
      });


    // Check if this is a paid leave date
    const isPaidLeaveDate = paidLeaveDates[date];
    
    // Create new breakdown or paid leave job entry
    const newJob = {
      id: isPaidLeaveDate ? 'PAID_LEAVE' : 'BREAKDOWN',
      job_date: date,
      start_time: date, // Just the date for paid leave
      end_time: date, // Just the date for paid leave
      client: isPaidLeaveDate ? 'Paid Leave Entry' : 'Breakdown Entry',
      job_types: jobTypes,
      workHours: distributedHours,
      regularHours: distributedHours,
      isBreakdown: !isPaidLeaveDate, // Flag to identify breakdown entries
      isPaidLeave: isPaidLeaveDate, // Flag to identify paid leave entries
      breakdownDetails: {
        normalHours: parseFloat(entry.normalHours) || 0,
        nightDifferential: nightDiffHours, // Use manual value if provided, otherwise auto-calculated
        overtime: parseFloat(entry.overtime) || 0,
        regularHoliday: parseFloat(entry.regularHoliday) || 0,
        specialHoliday: parseFloat(entry.specialHoliday) || 0,
        customEntries: (entry.customEntries || [])
          .filter(custom => custom != null)
          .map(custom => ({
            label: custom.label || '',
            labelType: custom.labelType || '',
            hours: parseFloat(custom.hours) || 0
          })),
        hourlyRate: baseRate
      }
    };

    // Add to payroll data
    setPayrollData((prev) => {
      const updated = [...prev, newJob];
      // Sort by date and time
      return updated.sort((a, b) => {
        const dateA = new Date(a.job_date).getTime();
        const dateB = new Date(b.job_date).getTime();
        if (dateA !== dateB) return dateA - dateB;
        
        // Manual and breakdown entries go to the end of the same date
        if ((a.isManual || a.isBreakdown) && !(b.isManual || b.isBreakdown)) return 1;
        if (!(a.isManual || a.isBreakdown) && (b.isManual || b.isBreakdown)) return -1;
        return 0;
      });
    });

    // Clear the breakdown form after saving
    setManualEntries((prev) => ({
      ...prev,
      [date]: {
        workHours: prev[date]?.workHours || '',
        normalHours: '',
        nightDifferential: '',
        overtime: '',
        regularHoliday: '',
        specialHoliday: '',
        customEntries: []
      }
    }));

    setSuccessMessage("Breakdown saved successfully!");
  };

  // Build the final calculation payload (HOURS ONLY).
  // This will be passed to PayrollFinalCalculation, which will handle
  // rate multipliers, pay amounts, and CSV/PDF generation.
  const buildFinalCalculationData = () => {
    // If there is no search or no payroll data, bail out
    if (!searchFilter?.startDate || !searchFilter?.endDate || !payrollData?.length) {
      return null;
    }

    // 1) Collect total hours per category
    const breakdownTotals = {
      normalHours: 0,
      nightDifferential: 0,
      overtime: 0,
      regularHoliday: 0,
      specialHoliday: 0,
      customEntries: {}, // { label: totalHours }
    };

    // Group payroll data by date
    const groupedByDate = payrollData.reduce((acc, job) => {
      const date = job.job_date;
      if (!date) return acc;
      if (!acc[date]) acc[date] = [];
      acc[date].push(job);
      return acc;
    }, {});

    // Get all dates in the selected period
    const allDatesInRange = getAllDatesInRange(
      searchFilter.startDate,
      searchFilter.endDate
    );

    // Dates that actually matter (have job orders or paid leave)
    const datesWithJobOrdersOrPaidLeave = allDatesInRange.filter((date) => {
      const jobsForDate = groupedByDate[date] || [];
      return jobsForDate.length > 0 || paidLeaveDates[date];
    });

    // Use your existing helper (auto night diff from spans)
    const calcNightDiffForDateLocal = (date) => {
      return calculateNightDiffForDate(
        date,
        spansByDate,
        payrollData,
        nightStart,
        nightEnd,
        calculateNightDifferentialFromSpans
      );
    };

    // Main loop – aggregate all the HOURS (no money here)
    datesWithJobOrdersOrPaidLeave.forEach((date) => {
      const jobsForDate = groupedByDate[date] || [];
      const hasActualJobOrders = jobsForDate.some(
        (job) => !job.isPaidLeave && !job.isManual && !job.isBreakdown
      );

      if (hasActualJobOrders) {
        // WITH job orders → use manual entries (real-time)
        const entry = getManualEntryForDate(date) || {};

        // Night differential: manual override (if filled) else auto-calculated
        const manualNightRaw = entry.nightDifferential;
        const hasManualNight =
          manualNightRaw !== "" &&
          manualNightRaw !== undefined &&
          manualNightRaw !== null;

        const manualNight =
          hasManualNight && !Number.isNaN(parseFloat(manualNightRaw))
            ? parseFloat(manualNightRaw)
            : 0;

        const autoNight = parseFloat(calcNightDiffForDateLocal(date)) || 0;
        const nightDiffForDate = hasManualNight ? manualNight : autoNight;

        breakdownTotals.normalHours += parseFloat(entry.normalHours) || 0;
        breakdownTotals.nightDifferential += nightDiffForDate;
        breakdownTotals.overtime += parseFloat(entry.overtime) || 0;
        breakdownTotals.regularHoliday += parseFloat(entry.regularHoliday) || 0;
        breakdownTotals.specialHoliday += parseFloat(entry.specialHoliday) || 0;

        // Custom entries (from manualEntries)
        if (entry.customEntries && Array.isArray(entry.customEntries)) {
          entry.customEntries.forEach((custom) => {
            if (!custom || !custom.label) return;
            const label = custom.label.trim();
            const rawValue = parseFloat(custom.hours) || 0;
            if (!label || rawValue <= 0) return;

            // Convert SIL credits to hours (1 credit = 8 hours)
            const hours = (custom.labelType === 'sil' || label === 'Service Incentive Leave') 
              ? rawValue * 8 
              : rawValue;

            if (!breakdownTotals.customEntries[label]) {
              breakdownTotals.customEntries[label] = 0;
            }
            breakdownTotals.customEntries[label] += hours;
          });
        }
      } else {
        // WITHOUT job orders → use saved breakdownDetails on paid-leave / breakdown rows
        jobsForDate.forEach((job) => {
          if ((job.isBreakdown || job.isPaidLeave) && job.breakdownDetails) {
            const details = job.breakdownDetails;

            breakdownTotals.normalHours += details.normalHours || 0;
            breakdownTotals.nightDifferential += details.nightDifferential || 0;
            breakdownTotals.overtime += details.overtime || 0;
            breakdownTotals.regularHoliday += details.regularHoliday || 0;
            breakdownTotals.specialHoliday += details.specialHoliday || 0;

            if (details.customEntries && Array.isArray(details.customEntries)) {
              details.customEntries
                .filter((custom) => custom != null)
                .forEach((custom) => {
                  const label = (custom.label || "Unlabeled").trim();
                  const rawValue = custom.hours || 0;
                  if (!label || rawValue <= 0) return;

                  // Convert SIL credits to hours (1 credit = 8 hours)
                  const hours = (custom.labelType === 'sil' || label === 'Service Incentive Leave') 
                    ? rawValue * 8 
                    : rawValue;

                  if (!breakdownTotals.customEntries[label]) {
                    breakdownTotals.customEntries[label] = 0;
                  }
                  breakdownTotals.customEntries[label] += hours;
                });
            }
          }
        });
      }
    });

    // 2) Grand total hours (distributed hours across categories)
    const grandTotalHours =
      breakdownTotals.normalHours +
      breakdownTotals.nightDifferential +
      breakdownTotals.overtime +
      breakdownTotals.regularHoliday +
      breakdownTotals.specialHoliday +
      Object.values(breakdownTotals.customEntries).reduce(
        (sum, hours) => sum + (hours || 0),
        0
      );

    // 3) Total deducted HOURS (no money yet)
    const totalDeductedHours = payrollData
      .filter((job) => job.isDeduction)
      .reduce((sum, job) => sum + (job.workHours || 0), 0);

    // 4) TOTAL "ADD WORK HOURS" (manual entries)
    const totalAddWorkHours = payrollData
      .filter((job) => job.isManual && !job.isDeduction && !job.isBreakdown)
      .reduce((sum, job) => sum + (job.workHours || 0), 0);

    // For now, this page only cares about HOURS.
    // The next page will apply hourlyRate + multipliers + incomes/deductions.
    return {
      breakdownTotals,
      grandTotalHours,
      totalDeductedHours,
      uniqueGrandTotalHours, // already existing
      totalAddWorkHours,     // ✅ NEW: pass Add Work Hours forward
    };
  };




  const handleGoToFinalCalculationPage = async () => {
    if (!hasSearched || payrollData.length === 0) {
      setErrorMessage("Please run a payroll search first before viewing the final calculation.");
      return;
    }

    const calculationData = buildFinalCalculationData();
    if (!calculationData) {
      setErrorMessage("Unable to build final calculation data. Please try searching again.");
      return;
    }

    // Calculate total SIL credits used from saved payroll data (not manual entries)
    let totalSilCreditsUsed = 0;
    
    // Check saved breakdown entries in payroll data
    payrollData.forEach(job => {
      if ((job.isBreakdown || job.isPaidLeave) && job.breakdownDetails?.customEntries) {
        job.breakdownDetails.customEntries.forEach(entry => {
          if ((entry.labelType === 'sil' || entry.label === 'Service Incentive Leave') && entry.hours) {
            const credits = parseFloat(entry.hours) || 0;
            totalSilCreditsUsed += credits;
          }
        });
      }
    });

    // Deduct SIL credits if any were used
    if (totalSilCreditsUsed > 0 && searchFilter?.employee?.id) {
      try {
        const result = await deductSILCredits(searchFilter.employee.id, totalSilCreditsUsed);
        if (result) {
          setSuccessMessage(`${totalSilCreditsUsed} SIL credit(s) deducted successfully. Remaining: ${result.remaining_credits}`);
        } else {
          console.warn("Failed to deduct SIL credits, but continuing to final calculation");
        }
      } catch (error) {
        console.error("Error deducting SIL credits:", error);
        // Don't block navigation even if deduction fails
      }
    }

    setFinalCalcData(calculationData);

    navigate("/payroll-final-calculation", {
      state: {
        calculationData,
        searchFilter,
        hourlyRate,          // the next page can still read / edit this
        additionalIncomes,   // optional; you may move the forms later
        deductions,
        rateMultipliers,
      },
    });
  };


  return (
    <BaseWrapper
      titleIcon={<PaymentsIcon />}
      title="Payroll"
      subTitle="Manage Payroll Reports"
      subTitleRoute={"/payroll-reports"}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid item xs={12}>
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid item xs={12} md={12} lg={4}>
            <Autocomplete
              freeSolo
              options={techniciansCache}
              getOptionLabel={(technician) => technician?.name || ""}
              value={searchFilter.employee || null}
              onChange={(event, newValue) =>
                handleSearchEmployee(event, newValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Technician Name"
                  variant="outlined"
                  fullWidth
                  error={!!error}
                  helperText={error || ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={2}>
            <TextField
              label="Start Date"
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              value={searchFilter.startDate}
              onChange={(e) => {
                const newStartDate = e.target.value;
                setSearchFilter((prev) => ({
                  ...prev,
                  startDate: newStartDate,
                  // If end date is before new start date, update it to match start date
                  endDate: prev.endDate < newStartDate ? newStartDate : prev.endDate,
                }));
              }}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={2}>
            <TextField
              label="End Date"
              type="date"
              variant="outlined"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              value={searchFilter.endDate}
              inputProps={{
                min: getMinEndDate(searchFilter.startDate), // Set minimum date to start date
              }}
              onChange={(e) => {
                const newEndDate = e.target.value;
                // Only allow end date if it's not before start date
                if (!searchFilter.startDate || newEndDate >= searchFilter.startDate) {
                  setSearchFilter((prev) => ({
                    ...prev,
                    endDate: newEndDate,
                  }));
                }
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={2}
            container
            justifyContent={{
              xs: "center",
              sm: "center",
              md: "flex-start",
              lg: "flex-start",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearchButtonClick}
              disabled={isLoading}
            >
              Search
            </Button>
          </Grid>
          
          {/* Night Hours Section */}
          {/* {hasSearched && payrollData.length > 0 && ( */}
            <Grid item xs={12} sm={12} md={4}>
              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", mb: 1, color: "#1e5d45" }}
                >
                  Modify Night Hours
                </Typography>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <TimePicker
                        label="Night Start"
                        ampm
                        minutesStep={1}
                        value={dayjs(nightStart, "HH:mm")}
                        onChange={(newValue) => {
                          if (!newValue) return;
                          setNightStart(newValue.format("HH:mm"));
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TimePicker
                        label="Night End"
                        ampm
                        minutesStep={1}
                        value={dayjs(nightEnd, "HH:mm")}
                        onChange={(newValue) => {
                          if (!newValue) return;
                          setNightEnd(newValue.format("HH:mm"));
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </Card>
            </Grid>
          {/* )} */}
        </Grid>
      </Grid>
      <StyledHr />
      
      {/* Placeholder content - will show when there's actual data */}
      {hasSearched && searchFilter.employee && searchFilter.startDate && searchFilter.endDate ? (
        <Card variant="outlined" sx={{ padding: "20px" }}>
          {/* Group payroll data by date */}
          {(() => {
            // Get all dates in the selected range
            const allDatesInRange = getAllDatesInRange(searchFilter.startDate, searchFilter.endDate);
            
            // Group jobs by date
            const groupedByDate = payrollData.reduce((acc, job) => {
              const date = job.job_date || 'No Date';
              if (!acc[date]) {
                acc[date] = [];
              }
              acc[date].push(job);
              return acc;
            }, {});

            // Ensure all dates in range are represented in groupedByDate
            allDatesInRange.forEach(date => {
              if (!groupedByDate[date]) {
                groupedByDate[date] = [];
              }
            });

            // Sort dates
            const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
              if (a === 'No Date') return 1;
              if (b === 'No Date') return -1;
              return new Date(a) - new Date(b);
            });

            // Separate dates into two categories
            const datesWithJobOrdersOrPaidLeave = [];
            const datesWithoutJobOrdersOrPaidLeave = [];

            sortedDates.forEach(date => {
              const jobsForDate = groupedByDate[date];
              const hasJobOrders = jobsForDate.some(job => !job.isManual && !job.isBreakdown && !job.isPaidLeave);
              const hasPaidLeave = paidLeaveDates[date];

              if (hasJobOrders || hasPaidLeave) {
                datesWithJobOrdersOrPaidLeave.push(date);
              } else {
                datesWithoutJobOrdersOrPaidLeave.push(date);
              }
            });

            // Render function for date rows
            const renderDateRow = (date, dateIndex) => {
              const jobsForDate = groupedByDate[date];
              // Calculate daily hours excluding deduction entries
              const dailyHours = jobsForDate
                .filter(job => !job.isDeduction)
                .reduce((sum, job) => sum + (job.workHours || 0), 0);
              // Check if there are any actual job orders (not manual, breakdown, deduction, or paid leave)
              const hasNoJobOrders = !jobsForDate.some(job => !job.isManual && !job.isBreakdown && !job.isPaidLeave && !job.isDeduction);

              return (
                <Box key={dateIndex} sx={{ mb: 4 }}>
                  {/* Date Header */}
                  <Box
                    sx={{
                      backgroundColor: hasNoJobOrders && !paidLeaveDates[date] ? "#757575" : "#1e5d45",
                      color: "white",
                      padding: "12px 16px",
                      borderRadius: "8px 8px 0 0",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 0
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {new Date(date).toLocaleDateString('en-US', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </Typography>
                      {hasNoJobOrders && !paidLeaveDates[date] && (
                        <Chip 
                          label="No Job Orders" 
                          size="small" 
                          sx={{ 
                            backgroundColor: "rgba(255, 255, 255, 0.2)",
                            color: "white",
                            fontWeight: "bold"
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
                      {hasNoJobOrders && (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={paidLeaveDates[date] || false}
                              onChange={() => handlePaidLeaveToggle(date)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#4caf50',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  backgroundColor: '#4caf50',
                                },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                              Paid Leave
                            </Typography>
                          }
                          sx={{ mr: 2 }}
                        />
                      )}
                      <Typography variant="body1">
                        Jobs: <strong>{jobsForDate.filter(job => {
                          // Include paid leave entries explicitly by checking multiple conditions
                          if (job.isPaidLeave || job.id === 'PAID_LEAVE' || paidLeaveDates[job.job_date]) {
                            return true;
                          }
                          // Include regular job orders (exclude manual entries, deductions, and breakdowns)
                          return !job.isManual && !job.isDeduction && !job.isBreakdown;
                        }).length}</strong>
                      </Typography>
                      <Typography variant="body1">
                        Hours: <strong>{dailyHours.toFixed(2)}</strong>
                      </Typography>
                      <IconButton
                        onClick={() => handleToggleExpand(date, hasNoJobOrders)}
                        sx={{
                          color: "white",
                          transform: expandedDates[date] ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s',
                          cursor: (hasNoJobOrders && !paidLeaveDates[date]) ? 'not-allowed' : 'pointer',
                          opacity: (hasNoJobOrders && !paidLeaveDates[date]) ? 0.5 : 1
                        }}
                        size="small"
                        disabled={hasNoJobOrders && !paidLeaveDates[date]}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Table for this date */}
                  <TableContainer sx={{ border: "1px solid #e0e0e0", borderTop: "none" }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                          <TableCell sx={{ textAlign: "center" }}>
                            <b>Job ID</b>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <b>Start Time</b>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <b>End Time</b>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <b>Client</b>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <b>Job Type</b>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <b>Work Hours</b>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <b>Action</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {jobsForDate.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ textAlign: "center", py: 4, fontStyle: "italic", color: "#999" }}>
                              No job orders for this date. {!paidLeaveDates[date] && "Toggle 'Paid Leave' if this is a paid leave day."}
                            </TableCell>
                          </TableRow>
                        ) : (
                          jobsForDate.map((job, index) => (
                            <TableRow 
                              key={index} 
                              sx={{ 
                                "&:hover": { backgroundColor: "#f9f9f9" },
                                backgroundColor: job.isManual ? "#fffef0" : (job.isBreakdown ? "#f0f8ff" : (job.isPaidLeave ? "#e8f5e9" : (job.isDeduction ? "#ffebee" : "transparent")))
                              }}
                            >
                              <TableCell sx={{ textAlign: "center" }}>
                                {job.isManual ? (
                                  <Chip 
                                    label={job.id || ""} 
                                    size="small" 
                                    color="warning" 
                                    variant="outlined"
                                  />
                                ) : job.isBreakdown ? (
                                  <Chip 
                                    label={job.id || ""} 
                                    size="small" 
                                    color="info" 
                                    variant="outlined"
                                  />
                                ) : job.isPaidLeave ? (
                                  <Chip 
                                    label={job.id || ""} 
                                    size="small" 
                                    color="success" 
                                    variant="outlined"
                                  />
                                ) : job.isDeduction ? (
                                  <Chip 
                                    label={job.id || ""} 
                                    size="small" 
                                    color="error" 
                                    variant="outlined"
                                  />
                                ) : (
                                  job.id || ""
                                )}
                              </TableCell>                            
                            {job.isManual || job.isBreakdown || job.isDeduction ? (
                              <TableCell
                                colSpan={2}
                                sx={{ textAlign: "center", fontStyle: "italic", color: "#666" }}
                              >
                                {formatTo12Hour(job.start_time) || ""}
                              </TableCell>
                            ) : (
                              <>
                                <TableCell sx={{ textAlign: "center" }}>
                                  {formatTo12Hour(job.start_time) || ""}
                                </TableCell>
                                <TableCell sx={{ textAlign: "center" }}>
                                  {formatTo12Hour(job.end_time) || ""}
                                </TableCell>
                              </>
                            )}
                            <TableCell sx={{ textAlign: "center" }}>{job.client || ""}</TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {job.job_types.length > 0 ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    justifyContent: "center",
                                  }}
                                >
                                  {job.job_types.map((type, i) => (
                                    <Chip
                                      key={i}
                                      label={type.replace(/_/g, " ")}
                                      variant="outlined"
                                      size="small"
                                      color={job.isManual ? "warning" : (job.isBreakdown ? "info" : (job.isDeduction ? "error" : "default"))}
                                    />
                                  ))}
                                </Box>
                              ) : (
                                ""
                              )}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <strong style={{ color: job.isDeduction ? "#d32f2f" : "inherit" }}>
                                {job.workHours?.toFixed(2) || "0.00"} hrs
                              </strong>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {job.isManual || job.isBreakdown || job.isPaidLeave || job.isDeduction ? (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => handleDeleteManualEntry(job.id, job.job_date)}
                                  sx={{
                                    textTransform: "none",
                                    padding: "6px 12px",
                                    fontSize: "12px",
                                    "&:hover": {
                                      backgroundColor: "#d32f2f",
                                      color: "white",
                                    }
                                  }}
                                >
                                  Delete
                                </Button>
                                  ) : (
                                    <ButtonGroup
                                      variant="outlined"
                                      size="small"
                                      aria-label="action button group"
                                      sx={{ gap: 0, padding: 0 }}
                                    >
                                      {/* View icon – opens SR in new tab */}
                                      <Button
                                        onClick={() => handleOpenServiceReportInNewTab(job)}
                                        sx={{
                                          padding: "4px 6px",
                                          borderRadius: "4px",
                                          minWidth: "32px",
                                          "&:hover": {
                                            backgroundColor: "#1e5d45",
                                            color: "white",
                                            transform: "translateY(-1px)",
                                            boxShadow: 2,
                                          },
                                        }}
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </Button>

                                      {/* Edit icon – opens start/end time dialog */}
                                      <Button
                                        onClick={() => handleOpenEditTimesDialog(job)}
                                        sx={{
                                          padding: "4px 6px",
                                          borderRadius: "4px",
                                          minWidth: "32px",
                                          "&:hover": {
                                            backgroundColor: "#1976d2",
                                            color: "white",
                                            transform: "translateY(-1px)",
                                            boxShadow: 2,
                                          },
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </Button>
                                    </ButtonGroup>
                                  )}
                                </TableCell>
                          </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Manual Entry Form - Collapsible - Below the table */}
                  <Collapse in={expandedDates[date]}>
                    <Box
                      sx={{
                        backgroundColor: "#f0f8f5",
                        padding: "16px",
                        border: "1px solid #e0e0e0",
                        borderTop: "none",
                        borderRadius: "0 0 8px 8px"
                      }}
                    >
                      {/* Check if this date has actual job orders (excluding paid leave, manual, breakdown, and deduction) */}
                      {(() => {
                        const hasActualJobOrders = jobsForDate.some(job => !job.isPaidLeave && !job.isManual && !job.isBreakdown && !job.isDeduction);
                        
                        if (!hasActualJobOrders) {
                          // For dates without job orders - only show custom label section
                          return (
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2, color: "#1e5d45" }}>
                                {paidLeaveDates[date] ? "Add Paid Leave Hours" : "Add Custom Entry"}
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 2, color: "#666", fontStyle: "italic" }}>
                                {paidLeaveDates[date] 
                                  ? "This date is marked as paid leave. Add hours for paid leave below."
                                  : "No job orders found for this date. You can add custom entries below."}
                              </Typography>
                              <Grid container spacing={2}>
                                {/* Custom Entries */}
                                {getManualEntryForDate(date).customEntries?.filter(entry => entry != null).map((customEntry, idx) => (
                                  <React.Fragment key={customEntry.id}>
                                    <Grid item xs={12} sm={7}>
                                      {customEntry.labelType === 'custom' ? (
                                        <TextField
                                          label={`Label ${idx + 1}`}
                                          type="text"
                                          variant="outlined"
                                          fullWidth
                                          size="small"
                                          value={customEntry.label}
                                          onChange={(e) => {
                                            let value = e.target.value;
                                            // Capitalize first letter of each word
                                            value = value.replace(/\b\w/g, (char) => char.toUpperCase());
                                            setManualEntries((prev) => ({
                                              ...prev,
                                              [date]: {
                                                ...getManualEntryForDate(date),
                                                customEntries: getManualEntryForDate(date).customEntries.filter(e => e != null).map((entry, i) => 
                                                  i === idx ? { ...entry, label: value } : entry
                                                )
                                              }
                                            }));
                                          }}
                                          onBlur={(e) => {
                                            // Remove leading/trailing spaces only when user leaves the field
                                            let value = e.target.value.trim();
                                            if (value !== customEntry.label) {
                                              setManualEntries((prev) => ({
                                                ...prev,
                                                [date]: {
                                                  ...getManualEntryForDate(date),
                                                  customEntries: getManualEntryForDate(date).customEntries.filter(e => e != null).map((entry, i) => 
                                                    i === idx ? { ...entry, label: value } : entry
                                                  )
                                                }
                                              }));
                                            }
                                          }}
                                          placeholder="e.g., Training, Meeting"
                                          InputProps={{
                                            endAdornment: (
                                              <InputAdornment position="end">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => {
                                                    setManualEntries((prev) => ({
                                                      ...prev,
                                                      [date]: {
                                                        ...getManualEntryForDate(date),
                                                        customEntries: getManualEntryForDate(date).customEntries.filter(e => e != null).map((entry, i) => 
                                                          i === idx ? { ...entry, labelType: '', label: '' } : entry
                                                        )
                                                      }
                                                    }));
                                                  }}
                                                  sx={{ mr: -1 }}
                                                >
                                                  <ExpandMoreIcon />
                                                </IconButton>
                                              </InputAdornment>
                                            )
                                          }}
                                        />
                                      ) : (
                                        <FormControl fullWidth size="small">
                                          <InputLabel id={`label-type-${idx}`}>{`Label ${idx + 1}`}</InputLabel>
                                          <Select
                                            labelId={`label-type-${idx}`}
                                            value={customEntry.labelType || ''}
                                            label={`Label ${idx + 1}`}
                                            onChange={(e) => {
                                              const selectedType = e.target.value;
                                              let newLabel = '';
                                              
                                              // Set label based on selected type
                                              if (selectedType === 'sil') {
                                                newLabel = 'Service Incentive Leave';
                                              } else if (selectedType === 'custom') {
                                                newLabel = '';
                                              }
                                              
                                              setManualEntries((prev) => ({
                                                ...prev,
                                                [date]: {
                                                  ...getManualEntryForDate(date),
                                                  customEntries: getManualEntryForDate(date).customEntries.filter(e => e != null).map((entry, i) => 
                                                    i === idx ? { ...entry, labelType: selectedType, label: newLabel } : entry
                                                  )
                                                }
                                              }));
                                            }}
                                          >
                                            <MenuItem value="sil" disabled={availableSilCredits !== null && availableSilCredits <= 0}>
                                              Service Incentive Leave
                                              {availableSilCredits !== null && availableSilCredits <= 0 && (
                                                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                                  (No credits available)
                                                </Typography>
                                              )}
                                            </MenuItem>
                                            <MenuItem value="custom">Custom</MenuItem>
                                          </Select>
                                        </FormControl>
                                      )}
                                    </Grid>
                                    <Grid item xs={10} sm={4}>
                                      <TextField
                                        label={customEntry.labelType === 'sil' ? "Credits" : "Hours"}
                                        type="number"
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        inputProps={{ 
                                          step: customEntry.labelType === 'sil' ? "0.5" : "0.01", 
                                          min: "0" 
                                        }}
                                        value={customEntry.hours}
                                        onChange={(e) => {
                                          setManualEntries((prev) => ({
                                            ...prev,
                                            [date]: {
                                              ...getManualEntryForDate(date),
                                              customEntries: getManualEntryForDate(date).customEntries.filter(e => e != null).map((entry, i) => 
                                                i === idx ? { ...entry, hours: e.target.value } : entry
                                              )
                                            }
                                          }));
                                        }}
                                        placeholder={customEntry.labelType === 'sil' ? "0.5" : "0.00"}
                                        error={customEntry.labelType === 'sil' && availableSilCredits !== null && parseFloat(customEntry.hours || 0) > availableSilCredits}
                                        helperText={
                                          customEntry.labelType === 'sil' && availableSilCredits !== null && parseFloat(customEntry.hours || 0) > availableSilCredits
                                            ? `Exceeds available credits (${availableSilCredits})`
                                            : ''
                                        }
                                      />
                                    </Grid>
                                    <Grid item xs={2} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <IconButton
                                        color="error"
                                        size="small"
                                        onClick={() => {
                                          setManualEntries((prev) => ({
                                            ...prev,
                                            [date]: {
                                              ...getManualEntryForDate(date),
                                              customEntries: getManualEntryForDate(date).customEntries.filter((_, i) => i !== idx)
                                            }
                                          }));
                                        }}
                                        sx={{
                                          '&:hover': {
                                            backgroundColor: 'transparent',
                                          }
                                        }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Grid>
                                  </React.Fragment>
                                ))}
                                
                                {/* Add Custom Label Button */}
                                <Grid item xs={12}>
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    fullWidth
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                      setManualEntries((prev) => ({
                                        ...prev,
                                        [date]: {
                                          ...getManualEntryForDate(date),
                                          customEntries: [...(getManualEntryForDate(date).customEntries || []), { 
                                            id: Date.now(), 
                                            labelType: '', // No default - user must choose
                                            label: '', 
                                            hours: '' 
                                          }]
                                        }
                                      }));
                                    }}
                                    sx={{
                                      height: "40px",
                                      textTransform: "none",
                                      borderStyle: "dashed",
                                      "&:hover": {
                                        borderStyle: "dashed",
                                      }
                                    }}
                                  >
                                    Add Custom Entry
                                  </Button>
                                </Grid>
                                
                                {/* Save Button */}
                                <Grid item xs={12}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    startIcon={<AddIcon />}
                                    onClick={() => handleSaveBreakdown(date)}
                                    disabled={!getManualEntryForDate(date).customEntries?.some(entry => 
                                      entry.label && parseFloat(entry.hours) > 0
                                    )}
                                    sx={{
                                      height: "40px",
                                      textTransform: "none",
                                      mt: 1
                                    }}
                                  >
                                    {paidLeaveDates[date] ? "Save Paid Leave" : "Save Custom Entry"}
                                  </Button>
                                </Grid>
                              </Grid>
                            </Box>
                          );
                        }
                        
                        // For dates WITH job orders - show full breakdown and manual hours
                        return (
                          <Grid container spacing={3} alignItems="flex-start">
                            {/* Left side - Hours Breakdown */}
                            <Grid item xs={12} md={5.8}>
                          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2, color: "#1e5d45" }}>
                            Hours Breakdown
                          </Typography>
                          <Grid container spacing={2}>
                            {/* Normal Hours */}
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Normal Hours"
                                type="number"
                                variant="outlined"
                                fullWidth
                                size="small"
                                inputProps={{ step: "0.01", min: "0" }}
                                value={getManualEntryForDate(date).normalHours}
                                onChange={(e) => handleAdditionalHoursChange(date, 'normalHours', e.target.value)}
                                placeholder="0.00"
                              />
                            </Grid>
                            
                            {/* Night Differential - Auto-calculated but editable */}
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Night Differential"
                                type="number"
                                variant="outlined"
                                fullWidth
                                size="small"
                                inputProps={{ step: "0.01", min: "0" }}
                                value={
                                  getManualEntryForDate(date).nightDifferential !== '' && 
                                  getManualEntryForDate(date).nightDifferential !== undefined
                                    ? getManualEntryForDate(date).nightDifferential
                                    : calcNightDiffForDate(date)
                                }
                                onChange={(e) => handleAdditionalHoursChange(date, 'nightDifferential', e.target.value)}
                                placeholder={`Auto: ${calcNightDiffForDate(date)}`}
                              />
                            </Grid>
                            
                            {/* Overtime */}
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Overtime"
                                type="number"
                                variant="outlined"
                                fullWidth
                                size="small"
                                inputProps={{ step: "0.01", min: "0" }}
                                value={getManualEntryForDate(date).overtime}
                                onChange={(e) => handleAdditionalHoursChange(date, 'overtime', e.target.value)}
                                placeholder="0.00"
                              />
                            </Grid>
                            
                            {/* Regular Holiday */}
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Regular Holiday"
                                type="number"
                                variant="outlined"
                                fullWidth
                                size="small"
                                inputProps={{ step: "0.01", min: "0" }}
                                value={getManualEntryForDate(date).regularHoliday}
                                onChange={(e) => handleAdditionalHoursChange(date, 'regularHoliday', e.target.value)}
                                placeholder="0.00"
                              />
                            </Grid>
                            
                            {/* Special Non-Working Holiday */}
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Special Non-Working Holiday"
                                type="number"
                                variant="outlined"
                                fullWidth
                                size="small"
                                inputProps={{ step: "0.01", min: "0" }}
                                value={getManualEntryForDate(date).specialHoliday}
                                onChange={(e) => handleAdditionalHoursChange(date, 'specialHoliday', e.target.value)}
                                placeholder="0.00"
                              />
                            </Grid>
                            
                            {/* Custom Labels Section - Multiple entries */}
                            {getManualEntryForDate(date).customEntries?.filter(entry => entry != null).map((customEntry, idx) => (
                              <React.Fragment key={customEntry.id}>
                                {/* Add empty grid item to push custom label to new row (column 1) */}
                                {idx === 0 && <Grid item xs={12} sm={6} />}
                                
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    label={`Custom Label ${idx + 1}`}
                                    type="text"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    value={customEntry.label}
                                    onChange={(e) => {
                                      let value = e.target.value;
                                      // Capitalize first letter of each word
                                      value = value.replace(/\b\w/g, (char) => char.toUpperCase());
                                      const updatedEntries = [...getManualEntryForDate(date).customEntries];
                                      updatedEntries[idx].label = value;
                                      handleAdditionalHoursChange(date, 'customEntries', updatedEntries);
                                    }}
                                    onBlur={(e) => {
                                      // Remove leading/trailing spaces only when user leaves the field
                                      let value = e.target.value.trim();
                                      if (value !== customEntry.label) {
                                        const updatedEntries = [...getManualEntryForDate(date).customEntries];
                                        updatedEntries[idx].label = value;
                                        handleAdditionalHoursChange(date, 'customEntries', updatedEntries);
                                      }
                                    }}
                                    placeholder="e.g., Training"
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    label="Hours"
                                    type="number"
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    value={customEntry.hours}
                                    onChange={(e) => {
                                      const updatedEntries = [...getManualEntryForDate(date).customEntries];
                                      updatedEntries[idx].hours = e.target.value;
                                      handleAdditionalHoursChange(date, 'customEntries', updatedEntries);
                                    }}
                                    placeholder="0.00"
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <IconButton
                                            color="error"
                                            size="small"
                                            edge="end"
                                            onClick={() => {
                                              const updatedEntries = getManualEntryForDate(date).customEntries.filter((_, i) => i !== idx);
                                              handleAdditionalHoursChange(date, 'customEntries', updatedEntries);
                                            }}
                                            sx={{
                                              '&:hover': {
                                                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                                              }
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </InputAdornment>
                                      )
                                    }}
                                  />
                                </Grid>
                              </React.Fragment>
                            ))}
                            
                            {/* Add Custom Label Button */}
                            <Grid item xs={12}>
                              <Button
                                variant="outlined"
                                color="primary"
                                fullWidth
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  const currentEntries = getManualEntryForDate(date).customEntries || [];
                                  const newEntry = {
                                    id: Date.now() + Math.random(), // Unique ID
                                    label: '',
                                    hours: ''
                                  };
                                  handleAdditionalHoursChange(date, 'customEntries', [...currentEntries, newEntry]);
                                }}
                                sx={{
                                  height: "40px",
                                  textTransform: "none",
                                  borderStyle: "dashed",
                                  "&:hover": {
                                    borderStyle: "dashed",
                                  }
                                }}
                              >
                                Add Custom Label
                              </Button>
                            </Grid>
                            
                            {/* Display Total Hours Summary */}
                            <Grid item xs={12}>
                              {(() => {
                                const entry = getManualEntryForDate(date);

                                // 1) Custom entries hours
                                const customEntriesHours = (entry.customEntries || [])
                                  .filter((custom) => custom != null)
                                  .reduce((sum, custom) => {
                                    return sum + (parseFloat(custom.hours) || 0);
                                  }, 0);

                                // 2) Night diff: manual override if provided, else auto-calculated
                                const nightDiffHours =
                                  entry.nightDifferential !== "" &&
                                  entry.nightDifferential !== undefined &&
                                  entry.nightDifferential !== null
                                    ? parseFloat(entry.nightDifferential) || 0
                                    : parseFloat(calcNightDiffForDate(date)) || 0;

                                // 3) Breakdown HOURS (excluding custom) used for validation + pay later
                                const breakdownHours =
                                  (parseFloat(entry.normalHours) || 0) +
                                  nightDiffHours +
                                  (parseFloat(entry.overtime) || 0) +
                                  (parseFloat(entry.regularHoliday) || 0) +
                                  (parseFloat(entry.specialHoliday) || 0);

                                // 4) Total distributed hours (for display, includes custom)
                                const distributedHours = breakdownHours + customEntriesHours;

                                // 5) Actual job order hours (exclude manual + deductions)
                                const actualJobHours = jobsForDate
                                  .filter((job) => !job.isDeduction && !job.isManual)
                                  .reduce((sum, job) => sum + (job.workHours || 0), 0);

                                // 6) Manually added HOURS (Add Work Hours rows) for this date
                                const manualHoursForDate = jobsForDate
                                  .filter((job) => job.isManual && !job.isDeduction && !job.isBreakdown)
                                  .reduce((sum, job) => sum + (job.workHours || 0), 0);

                                // 7) Deducted HOURS for this date
                                const deductedHoursForDate = jobsForDate
                                  .filter((job) => job.isDeduction)
                                  .reduce((sum, job) => sum + (job.workHours || 0), 0);

                                // 8) Unique hours (no overlap) from the uniqueDayTotals map
                                const uniqueDayTotal = uniqueDayTotals[date] || { minutes: 0, hours: 0 };
                                const uniqueHours = uniqueDayTotal.hours;

                                // 9) Effective “budget” for distribution:
                                //    Unique + manual – deducted
                                const availableHoursForBreakdown =
                                  uniqueHours + manualHoursForDate - deductedHoursForDate;

                                // 10) Compare breakdown (excluding custom) vs available budget
                                const tolerance = 0.01;
                                const isExceeded =
                                  breakdownHours > availableHoursForBreakdown + tolerance;

                                const remaining = availableHoursForBreakdown - breakdownHours;

                                return (
                                  <Box
                                    sx={{
                                      backgroundColor: isExceeded ? "#fff3e0" : "#e8f5e9",
                                      padding: "12px",
                                      borderRadius: "4px",
                                      border: `1px solid ${isExceeded ? "#ff9800" : "#1e5d45"}`,
                                      mt: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: "bold",
                                        color: isExceeded ? "#ff9800" : "#1e5d45",
                                        mb: 1,
                                      }}
                                    >
                                      {isExceeded
                                        ? "⚠️ Warning: Hours Distribution Exceeded!"
                                        : "✓ Hours Summary:"}
                                    </Typography>

                                    {/* Job order hours – only actual JOs (no manual, no deduction) */}
                                    <Typography
                                      variant="body2"
                                      sx={{ fontSize: "0.85rem", color: "#555" }}
                                    >
                                      Job Order Hours:{" "}
                                      <strong>{actualJobHours.toFixed(2)} hrs</strong>
                                    </Typography>

                                    {/* Unique (no-overlap) hours for this day */}
                                    <Typography
                                      variant="body2"
                                      sx={{ fontSize: "0.85rem", color: "#555" }}
                                    >
                                      Unique Hours:{" "}
                                      <strong>{uniqueHours.toFixed(2)} hrs</strong>
                                    </Typography>

                                    {/* NEW: Manually Added Hours (Add Work Hours rows) */}
                                    <Typography
                                      variant="body2"
                                      sx={{ fontSize: "0.85rem", color: "#555" }}
                                    >
                                      Manually Added Hours:{" "}
                                      <strong>{manualHoursForDate.toFixed(2)} hrs</strong>
                                    </Typography>

                                    {/* NEW: Deducted Hours line, shown under Manually Added Hours */}
                                    <Typography
                                      variant="body2"
                                      sx={{ fontSize: "0.85rem", color: "#555" }}
                                    >
                                      Deducted Hours:{" "}
                                      <strong>{deductedHoursForDate.toFixed(2)} hrs</strong>
                                    </Typography>

                                    {/* Distributed hours (breakdown + custom) */}
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.85rem",
                                        color: isExceeded ? "#ff9800" : "#555",
                                        fontWeight: isExceeded ? "bold" : "normal",
                                      }}
                                    >
                                      Distributed Hours:{" "}
                                      <strong>{distributedHours.toFixed(2)} hrs</strong>
                                    </Typography>

                                    {/* Remaining now uses (Unique + Manual – Deducted) – Breakdown */}
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.85rem",
                                        color: isExceeded
                                          ? "#ff9800"
                                          : remaining === 0
                                          ? "#2e7d32"
                                          : "#ff9800",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {isExceeded
                                        ? `Over by: ${Math.abs(remaining).toFixed(2)} hrs`
                                        : remaining === 0
                                        ? "✓ All hours distributed"
                                        : `Remaining: ${remaining.toFixed(2)} hrs`}
                                    </Typography>

                                    {isExceeded && (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.8rem",
                                          color: "#ff9800",
                                          fontStyle: "italic",
                                          mt: 1,
                                        }}
                                      >
                                        Note: You can still save this breakdown, but it exceeds the total
                                        available hours (Unique + Manually Added – Deducted) for this date.
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              })()}
                            </Grid>
                          </Grid>
                        </Grid>

                        {/* Vertical Divider */}
                        <Grid item xs={false} md={0.4} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'stretch' }}>
                          <Box
                            sx={{
                              width: '1px',
                              backgroundColor: '#1e5d45',
                              opacity: 0.3,
                              height: '100%',
                              minHeight: '600px'
                            }}
                          />
                        </Grid>

                        {/* Horizontal Divider for mobile */}
                        <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' }, my: 2 }}>
                          <Box
                            sx={{
                              height: '1px',
                              backgroundColor: '#1e5d45',
                              opacity: 0.3,
                              width: '100%'
                            }}
                          />
                        </Grid>

                        {/* Right side - Manual Work Hours */}
                        <Grid item xs={12} md={5.8}>
                          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2, color: "#1e5d45" }}>
                            Add Work Hours
                          </Typography>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Work Hours"
                                type="number"
                                variant="outlined"
                                fullWidth
                                size="small"
                                inputProps={{ step: "0.01", min: "0" }}
                                value={getManualEntryForDate(date).workHours}
                                onChange={(e) => handleManualEntryChange(date, e.target.value)}
                                placeholder="Enter hours (e.g., 8.5)"
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Remarks (Optional)"
                                type="text"
                                variant="outlined"
                                fullWidth
                                size="small"
                                value={getManualEntryForDate(date).workHoursRemarks || ''}
                                onChange={(e) => {
                                  setManualEntries((prev) => ({
                                    ...prev,
                                    [date]: {
                                      ...getManualEntryForDate(date),
                                      workHoursRemarks: e.target.value
                                    }
                                  }));
                                }}
                                placeholder="e.g., Extra work, Overtime"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                startIcon={<AddIcon />}
                                onClick={() => handleAddManualEntry(date)}
                                sx={{
                                  height: "40px",
                                  textTransform: "none"
                                }}
                              >
                                Add Hours
                              </Button>
                            </Grid>
                          </Grid>

                          {/* Deduct Work Hours Section */}
                          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2, mt: 4, color: "#d32f2f" }}>
                            Deduct Work Hours
                          </Typography>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Deduct Hours"
                                type="number"
                                variant="outlined"
                                fullWidth
                                size="small"
                                inputProps={{ step: "0.01", min: "0" }}
                                value={getManualEntryForDate(date).deductHours || ''}
                                onChange={(e) => {
                                  setManualEntries((prev) => ({
                                    ...prev,
                                    [date]: {
                                      ...getManualEntryForDate(date),
                                      deductHours: e.target.value
                                    }
                                  }));
                                }}
                                placeholder="Enter hours (e.g., 2.5)"
                                required
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Remarks (Optional)"
                                type="text"
                                variant="outlined"
                                fullWidth
                                size="small"
                                value={getManualEntryForDate(date).deductHoursRemarks || ''}
                                onChange={(e) => {
                                  setManualEntries((prev) => ({
                                    ...prev,
                                    [date]: {
                                      ...getManualEntryForDate(date),
                                      deductHoursRemarks: e.target.value
                                    }
                                  }));
                                }}
                                placeholder="e.g., Late, Absent hours"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Button
                                variant="contained"
                                color="error"
                                fullWidth
                                startIcon={<RemoveIcon />}
                                onClick={() => handleDeductManualEntry(date)}
                                sx={{
                                  height: "40px",
                                  textTransform: "none"
                                }}
                              >
                                Deduct Hours
                              </Button>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                        );
                      })()}
                    </Box>
                  </Collapse>
                </Box>
              );
            };

            return (
              <>
                {/* Section 1: Dates With Job Orders or Paid Leave */}
                {datesWithJobOrdersOrPaidLeave.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Box 
                      sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "space-between",
                        mb: 3, 
                        borderBottom: "3px solid #1e5d45",
                        paddingBottom: "8px",
                        gap: 2,
                        flexWrap: "wrap"
                      }}
                    >
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: "bold", 
                          color: "#1e5d45",
                        }}
                      >
                        Dates With Job Orders / Paid Leave
                      </Typography>
                    </Box>
                    {datesWithJobOrdersOrPaidLeave.map((date, index) => renderDateRow(date, `with-${index}`))}
                    
                    {/* Final Calculation Container */}

                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 1 }}>
                      {/* <Button
                        variant="outlined"
                        onClick={() => navigate("/payroll-reports")} // optional "reset"
                      >
                        Back to Payroll Search
                      </Button> */}

                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PaymentsIcon />}
                        onClick={handleGoToFinalCalculationPage}
                        disabled={!hasSearched || payrollData.length === 0}
                      >
                        Go to Final Calculation
                      </Button>
                    </Box>

                              
                  </Box>
                )}

                {/* Section 2: Dates Without Job Orders or Paid Leave */}
                {datesWithoutJobOrdersOrPaidLeave.length > 0 && (
                  <Box>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        mb: 3, 
                        fontWeight: "bold", 
                        color: "#757575",
                        borderBottom: "3px solid #757575",
                        paddingBottom: "8px"
                      }}
                    >
                      Dates Without Job Orders / Paid Leave
                    </Typography>
                    {datesWithoutJobOrdersOrPaidLeave.map((date, index) => renderDateRow(date, `without-${index}`))}
                  </Box>
                )}
              </>
            );
          })()}
        </Card>
      ) : (
        <Card variant="outlined" sx={{ padding: "20px", textAlign: "center" }}>
          <Typography variant="h6" color="textSecondary">
            Please select a technician and date range, then click Search to view payroll data.
          </Typography>
        </Card>
      )}

      {/* Edit Start/End Time Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditTimesDialog}
        maxWidth="xs"
        fullWidth 
      >
        <DialogTitle>Edit Job Time</DialogTitle>
        <DialogContent dividers>
          {selectedJobForEdit && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <Typography variant="subtitle2">
                Job ID: <strong>{selectedJobForEdit.id}</strong>
              </Typography>
              <Typography variant="subtitle2">
                Date: <strong>{selectedJobForEdit.original_job_date || selectedJobForEdit.job_date}</strong>
              </Typography>
              <Typography variant="subtitle2">
                Client: <strong>{selectedJobForEdit.client || ""}</strong>
              </Typography>

              <TextField
                label="Start Time"
                type="time"
                fullWidth
                value={editStartTime}
                onChange={(e) => setEditStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}   // 5-minute increments
              />
              <TextField
                label="End Time"
                type="time"
                fullWidth
                value={editEndTime}
                onChange={(e) => setEditEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditTimesDialog} disabled={editSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveEditedTimes}
            variant="contained"
            disabled={editSaving}
          >
            {editSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </BaseWrapper>
  );
}

export default PayrollReports;
